import type { ReactNode } from 'react'
import { Press } from '@dorixona/ui'
import { formatDayTime, formatPhone } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Select } from '../../ui/Select'
import type { Order, OrderStatus } from './api'
import { OrderStepper } from './OrderStepper'
import { STATUS_TONE, courierStatusText, pickedCount, providerText, type SlaView } from './model'
import {
  TRIP,
  courierTitle,
  nextStep,
  stageIcon,
  stageTitle,
  tripStep,
  waitsOutside,
  type NextStep,
} from './stage'

export interface OrderNowProps {
  order: Order
  /** Можно двигать заказ: право `orders:u` и заказ не закрыт. */
  canUpdate: boolean
  /** Отказаться аптека может, пока заказ не собран (R-37). */
  canDecline: boolean
  /** Назначить аптеку руками — сотрудник всей сети, зависшему заказу (R-38). */
  routeOptions: { value: number; label: string }[] | null
  sla: SlaView | null
  assignee: string
  /** Запрос по заказу уже идёт: второй раз не нажимаем. */
  busy: boolean
  onStep: (action: NextStep['action']) => void
  onPick: (lineId: number) => void
  onDecline: () => void
  onRoute: (storeId: number) => void
}

/**
 * «Сейчас» — главная карточка заказа: путь, текущий этап, его действие и то,
 * чем этап занят. Сборка — чек-листом, доставка — поездкой курьера. Остальная
 * карточка — справка; здесь то, что делают прямо сейчас.
 */
export function OrderNow(props: OrderNowProps) {
  const { order, canUpdate, sla } = props
  const step = canUpdate ? nextStep(order) : null
  const tone = STATUS_TONE[order.status]
  const tracking = order.ship === 'delivery' && (order.delivery !== null || isTrip(order.status))

  return (
    <section className="ui-card od-now" data-tone={tone} aria-labelledby="od-now">
      <OrderStepper order={order} sub={tripText(order)} waiting={waitsOutside(order.status)} />
      <div className="od-now-head">
        <span
          className="od-now-icon"
          data-waiting={waitsOutside(order.status) || undefined}
          aria-hidden="true"
        >
          <Icon name={stageIcon(order)} />
        </span>
        <div className="od-now-title">
          <h2 id="od-now">{stageTitle(order)}</h2>
          <StageFacts {...props} />
        </div>
        <div className="od-now-actions">
          {props.canDecline ? (
            <Press
              as="button"
              type="button"
              className="ui-btn"
              data-variant="secondary"
              squish="card"
              onPress={props.onDecline}
            >
              Отказаться
            </Press>
          ) : null}
          {step ? (
            <Press
              as="button"
              type="button"
              className="ui-btn"
              data-variant={step.fallback ? 'secondary' : undefined}
              squish="card"
              disabled={step.blocked || props.busy}
              aria-busy={props.busy || undefined}
              onPress={() => {
                props.onStep(step.action)
              }}
            >
              {props.busy ? <Icon name="i-sync" className="od-spin" /> : null}
              {step.label}
            </Press>
          ) : null}
        </div>
      </div>

      {sla?.state === 'late' ? (
        <div className="od-now-body">
          <div className="ui-alert" data-tone="danger" data-layout="row">
            <Icon name="i-alert" />
            <div className="txt">
              <b>Этап просрочен</b>
              {sla.text}
            </div>
          </div>
        </div>
      ) : null}

      {order.status === 'assembling' ? <PickList {...props} /> : null}

      {order.status === 'assembled' && order.ship === 'delivery' ? (
        <div className="od-now-body">
          <div className="ui-alert" data-tone="warning" data-layout="row">
            <Icon name="i-alert" />
            <div className="txt">
              <b>Служба доставки не приняла заявку — отправьте её ещё раз</b>
            </div>
          </div>
        </div>
      ) : null}

      {tracking ? <Trip order={order} /> : null}

      {props.routeOptions ? (
        <div className="od-now-body od-route">
          <div className="ui-field">
            <span className="ui-label" aria-hidden="true">
              Перевести в другую аптеку
            </span>
            <Select
              label="Перевести в другую аптеку"
              value={order.storeId}
              options={props.routeOptions}
              onChange={(storeId) => {
                // Выбор той же аптеки — не перевод: запрос не нужен.
                if (storeId !== order.storeId) props.onRoute(storeId)
              }}
            />
          </div>
        </div>
      ) : null}
    </section>
  )
}

/** Что сообщила служба о поездке — подписью под этапом «Ожидает курьера» или «Доставляется». */
function tripText(order: Order): string | undefined {
  if (order.ship !== 'delivery') return undefined
  if (order.status !== 'awaiting_courier' && order.status !== 'in_delivery') return undefined
  return TRIP[tripStep(order, order.delivery)]?.label.toLowerCase()
}

const isTrip = (status: OrderStatus) =>
  status === 'awaiting_courier' || status === 'in_delivery' || status === 'delivered'

/** Под заголовком этапа — короткие факты: сколько собрано, кто собирает, сроки. */
function StageFacts({ order, sla, assignee }: OrderNowProps) {
  const facts: ReactNode[] = []
  if (order.status === 'assembling') {
    facts.push(`Собрано ${pickedCount(order)} из ${order.lines.length}`)
  }
  if (order.assigneeId && !isTrip(order.status)) facts.push(`Сборщик: ${assignee}`)
  if (sla && sla.state !== 'late') {
    facts.push(
      <span className="ui-sla" data-state={sla.state} key="sla">
        <Icon name="i-clock" size={14} />
        {sla.text}
      </span>,
    )
  }
  if (order.reservationExpires && order.status === 'ready_for_pickup') {
    facts.push(`Бронь до ${formatDayTime(order.reservationExpires)}`)
  }
  if (order.status === 'awaiting_courier' || order.status === 'in_delivery') {
    facts.push(
      <span key="auto" className="od-auto">
        <Icon name="i-clock" size={14} />
        статусы присылает служба доставки
      </span>,
    )
  }
  if (order.completedAt) facts.push(formatDayTime(order.completedAt))
  if (facts.length === 0) return null
  return (
    <p className="od-now-facts">
      {facts.map((fact, i) => (
        <span key={i}>{fact}</span>
      ))}
    </p>
  )
}

/**
 * Чек-лист сборки: каждая позиция — строка во всю ширину с крупной отметкой и
 * количеством. Снять отметку нельзя — в API есть только «собрано».
 */
function PickList({ order, canUpdate, busy, onPick }: OrderNowProps) {
  const picked = pickedCount(order)
  const total = order.lines.length
  if (total === 0) return null
  return (
    <div className="od-now-body od-pick">
      <div className="ui-progress">
        <span
          className="bar"
          role="progressbar"
          aria-label={`Собрано позиций: ${picked} из ${total}`}
          aria-valuenow={picked}
          aria-valuemax={total}
        >
          <i style={{ width: `${Math.round((picked / total) * 100)}%` }} />
        </span>
        {picked} из {total}
      </div>
      <ul className="od-picklist">
        {order.lines.map((line) => (
          <li key={line.id} data-picked={line.picked || undefined}>
            <label>
              <input
                type="checkbox"
                checked={line.picked}
                disabled={!canUpdate || line.picked || busy}
                onChange={() => {
                  onPick(line.id)
                }}
              />
              <span className="name">{line.name}</span>
              <span className="qty">× {line.qty}</span>
              {line.picked ? (
                <span className="ui-status" data-tone="success">
                  собрано
                </span>
              ) : null}
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Поездка курьера: шкала этапов службы и карточка водителя. */
function Trip({ order }: { order: Order }) {
  const delivery = order.delivery
  const cancelled = delivery?.status === 'cancelled'
  const courier = delivery?.courier ?? null
  const provider = [
    providerText(delivery?.provider ?? ''),
    delivery?.tracking && `трек ${delivery.tracking}`,
  ]
    .filter(Boolean)
    .join(', ')
  return (
    <div className="od-now-body od-trip">
      {cancelled ? (
        <div className="ui-alert" data-tone="danger" data-layout="row">
          <Icon name="i-alert" />
          <div className="txt">
            <b>{courierStatusText('cancelled')}</b>
          </div>
        </div>
      ) : null}
      <div className="od-courier">
        <span className="od-courier-icon" aria-hidden="true">
          <Icon name="i-truck" />
        </span>
        <div className="who">
          <b>{courierTitle(courier)}</b>
          {courier?.name && courier.car ? <span>{courier.car}</span> : null}
          <span>{provider || 'заявка у службы ещё не создана'}</span>
        </div>
        {courier?.carNumber ? <span className="od-plate">{courier.carNumber}</span> : null}
        {courier?.phone ? (
          <a
            className="ui-btn"
            data-variant="secondary"
            data-size="sm"
            href={`tel:+${courier.phone.replace(/\D/g, '')}`}
          >
            <Icon name="i-phone" />
            {formatPhone(courier.phone)}
          </a>
        ) : null}
      </div>
    </div>
  )
}
